// Note: useQueryClient import removed as it's not currently used
import { useAuth } from "@/hooks/use-auth";
import { RefreshEmailsButtonV3 } from "@/components/RefreshEmailsButtonV3";
import { EmailProgress } from "@/components/EmailProgress";
import { useEmailRefreshStore } from "@/lib/store";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

interface EmailRefreshCardProps {
  isDataReady?: boolean;
}

export default function EmailRefreshCard({ isDataReady = false }: EmailRefreshCardProps) {
  const { user } = useAuth();
  const { isRefreshing } = useEmailRefreshStore();
  
  // Fetch email credentials to check if OAuth (using secure API)
  const { data: credentials } = useQuery({
    queryKey: ['email-credentials'],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', '/api/email-credentials/');
        if (!response.ok) {
          return null;
        }
        return await response.json();
      } catch (err) {
        console.warn('Failed to fetch email credentials:', err);
        return null;
      }
    },
    enabled: !!user
  });
  
  // Format date helper
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return "Not yet fetched";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true
    });
  };
  
  // Get the last refresh date from the store or fallback to user data
  const { lastRefreshDate } = useEmailRefreshStore();
  const displayDate = lastRefreshDate || user?.last_email_refresh;
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-neutral-200 lg:col-span-1">
      <h3 className="text-lg font-semibold text-neutral-800 mb-4 text-center">Email Refresh</h3>
      <div className="flex flex-col items-center justify-center py-4 sm:py-6">
        <div className="w-32 h-32 rounded-full flex items-center justify-center border-4 border-neutral-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-neutral-800">Now</p>
            <p className="text-sm text-neutral-500">available</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="w-full mt-3 sm:mt-4">
          <EmailProgress />
        </div>
        
        {/* Refresh Button */}
        <div className="mt-4 sm:mt-6 w-full">
          <RefreshEmailsButtonV3 
            isOAuth={user?.oauth_provider !== null && user?.oauth_provider !== undefined}
            oauthProvider={user?.oauth_provider || 'microsoft'}
            isDataReady={isDataReady}
          />
        </div>
        
        <p className="text-xs text-neutral-500 mt-2 text-center">
          Last refresh: {formatDate(displayDate)}
        </p>
        
        <p className="text-xs text-neutral-500 mt-3 sm:mt-4 text-center italic">
          Note: First email fetch may take 40-60 minutes depending on inbox size
        </p>
        
        {isRefreshing && (
          <p className="text-xs text-neutral-500 mt-2 text-center">
            Email processing continues in background - you can close this page.
          </p>
        )}
      </div>
    </div>
  );
}
